// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
import { LAWFUL_BASIS_DECLARATION_SURFACES } from './lawful-basis.js';
import { TRANSFER_MECHANISM_REGISTER_SURFACE, TRANSFER_MECHANISM_VERIFIED_NONE_SURFACE, } from './transfer-mechanism.js';
import { CONTROLLED_TESTING_PERSONNEL_REGISTER_SURFACE } from './controlled-testing-personnel.js';
import { PCF_SUPERVISION_REGISTER_SURFACES } from './pcf-supervision.js';
import { DATA_EXPLOITATION_REGISTER_SURFACES } from './data-exploitation.js';
import { CYBERSECURITY_ASSURANCE_INDEX_REGISTER_SURFACES } from './cybersecurity-assurance-index.js';
import { CUSTOMS_TAX_REGISTER_SURFACES } from './customs-tax-administration.js';
import { BIOMETRIC_CAPTURE_REGISTER_SURFACES } from './biometric-capture.js';
import { REGULATORY_FEE_SCHEDULE_REGISTER_SURFACE } from './regulatory-fees.js';
import { FOOD_TRACEABILITY_PACK_FORM_SURFACES, FOOD_TRACEABILITY_REGISTER_SURFACES, } from './food-traceability.js';
// ── Pack selector vocabulary (the ONLY selectors src may use) ──
/** `register_surface` values the pack's templates declare to feed this register. */
export const AML_REGISTER_SURFACES = {
    scoping: 'aml_reporting_entity_scoping',
    indicatorCoverage: 'aml_indicator_coverage',
    case: 'aml_suspicious_activity_case',
    beneficialOwnership: 'aml_beneficial_ownership',
    preLaunch: 'aml_pre_launch_risk_assessment',
    retentionScope: 'aml_records_retention_scope',
};
/**
 * Sprint 62.46.0 (work file ruling 15) — platform register surfaces whose
 * templates may be recorded ONLY through their own register, never as a
 * generic Compliance Forms instance. A template carrying one of these
 * `register_surface` values is refused by the form-instance create path: its
 * register enforces a restricted read (`aml_case:read`, an identical 404,
 * audited denials) that a form instance — readable with `forms:instance:read`
 * — would bypass. Platform surface ids only; never a framework, template or
 * option name.
 */
export const REGISTER_ONLY_SURFACES = new Set([
    AML_REGISTER_SURFACES.case,
    // Sprint 63.24.0 — the personnel template is read by its register, which is restricted
    // to controlled_testing_case:read. A generic Forms instance would open the same data
    // through forms:instance:read, the bypass the AML case surface closes.
    CONTROLLED_TESTING_PERSONNEL_REGISTER_SURFACE,
    // Sprint 62.50.0 (gate E12.5 pass 1, P1-1) — the nine supervision-information
    // templates are recorded only through their registers, which enforce the pack's
    // option rules, the user and record links and the per-register write grants; a
    // generic Forms instance would bypass every one of them.
    ...Object.values(PCF_SUPERVISION_REGISTER_SURFACES),
    // Sprint 62.51.0 (D5) — the data-exploitation templates are recorded only
    // through their registers, which enforce the pack's option flags, the route
    // vocabulary, the grant links and the manage grant; a generic Forms instance
    // of one would be a second record the register and its export never see.
    ...Object.values(DATA_EXPLOITATION_REGISTER_SURFACES),
    // Sprint 62.52.0 (D8) — the cybersecurity assurance index templates (the catalogue,
    // the period, the statistic and the supplier request) are recorded only through their
    // registers, which enforce the level-derived offer, the defined Mức, the evidence
    // marker and the membership read gate; a generic Forms instance would bypass all of it.
    ...Object.values(CYBERSECURITY_ASSURANCE_INDEX_REGISTER_SURFACES),
    // Sprint 62.55.0 — the ten customs tax-administration register templates are
    // recorded only through their registers (option rules, customs-record and
    // dossier links, per-register write grants); declared in the same change as the
    // templates (the 62.50.0 lesson).
    ...Object.values(CUSTOMS_TAX_REGISTER_SURFACES),
    // Sprint 62.56.0 (AC-D3) — the component, custody and readiness-review templates are
    // recorded only through their registers, which enforce the section offering, the
    // date order, the different-recorder rule and the per-register grants.
    ...Object.values(BIOMETRIC_CAPTURE_REGISTER_SURFACES),
    // Sprint 62.49.0 — the food establishment and certificate templates are recorded only
    // through their registers, which enforce the declared registration-document and
    // certificate-kind options, the second-person equivalence approval and the write
    // grant; a generic Forms instance of one would be a second record the register and
    // its export never see. Declared in the same change as the templates' surfaces (the
    // 62.50.0 lesson). The pack's report layout, lot record, recall template and
    // records-request log stay ordinary templates: the food module reads them, but an
    // organisation may still keep them as Forms.
    ...Object.values(FOOD_TRACEABILITY_REGISTER_SURFACES),
    // Sprint 62.56.0 (AC-D3, a defect found in passing) — the fee-schedule template is the
    // fee register's ROW TABLE: a generic Forms instance of it is a fee schedule nobody
    // reads, recorded outside the register that prices lines from it.
    REGULATORY_FEE_SCHEDULE_REGISTER_SURFACE,
]);
/**
 * Sprint 62.46.0 (gate E12.5 pass 1, P1-2) — platform register surfaces whose
 * installed templates are PACK-OWNED: the register reads its rules off their
 * option and field attributes (`two_person_policy_default`,
 * `programme_inactive`, `determination_outcome`, `requires_filing_record`,
 * `indicator_kind`, `counts_as_disposition`, `permits_launch`,
 * `disposition_requires`, the clock attributes), so a tenant edit of one is an
 * edit of the rule. Every Compliance Forms template mutation path (update,
 * uninstall, clone) refuses a template carrying one of these with 409
 * `TEMPLATE_REGISTER_LOCKED`; the pack installer writes them directly and a
 * re-install is how their content changes. Platform surface ids only; never a
 * framework, template or option name.
 */
export const PACK_LOCKED_REGISTER_SURFACES = new Set([
    ...Object.values(AML_REGISTER_SURFACES),
    // fix-sprint-20261003-0625 F11 — the lawful-basis declarations are pack-owned
    // the same way: the vocabulary reader reads the bases off their options.
    ...LAWFUL_BASIS_DECLARATION_SURFACES,
    // fix-sprint-20261003-0625 F28 (OD42) — and so are the transfer-mechanism
    // declarations, which the boot sync writes beside them.
    TRANSFER_MECHANISM_REGISTER_SURFACE,
    TRANSFER_MECHANISM_VERIFIED_NONE_SURFACE,
    // Sprint 63.24.0 — the personnel roles, qualification alternatives and outcomes are
    // read off this template's options; a tenant edit of one would change who is eligible.
    CONTROLLED_TESTING_PERSONNEL_REGISTER_SURFACE,
    // Sprint 62.50.0 (gate E12.5 pass 1, P1-1) — the supervision-information registers
    // read `requires_approval_reference`, `absence_statement_allowed`, `recipient_values`,
    // `applies_to_requirement_ids`, `clock_deadline_id` and the single routing option off
    // these templates; a tenant edit of one would be an edit of the rule.
    ...Object.values(PCF_SUPERVISION_REGISTER_SURFACES),
    // Sprint 62.51.0 (D5) — the data-exploitation registers read `records_grant`,
    // `records_refusal`, `requires_data_protection_plan`, `requires_retention_basis`,
    // `no_authority_held` and `applies_to_requirement_ids` off these templates; a
    // tenant edit of one would be an edit of the rule.
    ...Object.values(DATA_EXPLOITATION_REGISTER_SURFACES),
    // Sprint 62.52.0 (D8) — the registers read `applies_from_level`, `defined_ratings`,
    // `requires_explanation_and_evidence`, `authority_figure_floor`, `rank` and
    // `applies_to_requirement_ids` off these templates; a tenant edit of one would be an
    // edit of the rule.
    ...Object.values(CYBERSECURITY_ASSURANCE_INDEX_REGISTER_SURFACES),
    // Sprint 62.55.0 — the customs tax-administration registers read procedure ids,
    // register and received roles, admitted templates, clock ids and schedule rules
    // off these templates' options; a tenant edit of one would be an edit of the rule.
    ...Object.values(CUSTOMS_TAX_REGISTER_SURFACES),
    // Sprint 62.56.0 (AC-D3) — the biometric registers read `section_role`,
    // `applies_to_component_types`, `evidences_requirement_ids`, `represents_absence`,
    // `custody_outcome` and every field's `required` off these templates.
    ...Object.values(BIOMETRIC_CAPTURE_REGISTER_SURFACES),
    // Sprint 62.49.0 — the food registers read the registration-document options, the
    // certificate kinds and the field labels and requirements off their templates, and a
    // recall case's trigger reads its sources and `requires_authority_request` off the
    // recall template's options; a tenant edit of one would be an edit of the rule.
    ...Object.values(FOOD_TRACEABILITY_REGISTER_SURFACES),
    FOOD_TRACEABILITY_PACK_FORM_SURFACES.recall,
    // Sprint 62.56.0 (AC-D3, found in passing) — the fee register reads its rows,
    // rates, payer rules, transitional provision and collecting bodies off the
    // fee-schedule template; a tenant edit of it would re-price every line.
    REGULATORY_FEE_SCHEDULE_REGISTER_SURFACE,
]);
/**
 * The attribute names the register reads off installed options, fields and
 * templates. The pack declares the values; these are only the keys.
 *
 * On an OPTION:
 * - `programme_inactive` — a scoping role that switches the programme surfaces
 *   to their explained inactive state (a reason is then required);
 * - `two_person_policy_default` — a scoping role's default for the two-person
 *   rule, overridden by the organisation's own setting;
 * - `framework_indicator_code` — the catalogue code an indicator option stands
 *   for;
 * - `indicator_kind` — whether an indicator option is a catalogue indicator or
 *   one drawn from another provision;
 * - `disposition_requires` — the fields a coverage option needs filled before a
 *   row counts as disposed of;
 * - `counts_as_disposition` — `false` on a coverage option that is not a
 *   disposition (it blocks version approval);
 * - `determination_outcome` — which of {@link AML_DETERMINATION_OUTCOMES} a case
 *   disposition option records;
 * - `requires_filing_record` — a disposition under which a case closes as
 *   reported only with a filing record;
 * - `permits_launch` — an assessment decision option that permits the launch;
 * - `deadline_id` — on a records-retention `record_class` option, the installed
 *   record-retention deadline that is THAT class's period (fix-sprint-20261003-0545
 *   F27). The register-wide option attribute `validate:packs` resolves against
 *   the deadline records of the frameworks declaring the surface.
 *
 * On a FIELD:
 * - `clock_trigger_event` — the event a deadline record's clock runs from;
 * - `requires_with` — companion fields that must be filled with this one;
 * - `recordable_only_when_true` — boolean fields that must all be `true` before
 *   this one may hold a value.
 *
 * On a TEMPLATE:
 * - `indicator_catalogue_field` — the framework-record key holding the indicator
 *   catalogue the coverage register generates its rows from.
 */
export const AML_PACK_ATTR = {
    programmeInactive: 'programme_inactive',
    twoPersonPolicyDefault: 'two_person_policy_default',
    frameworkIndicatorCode: 'framework_indicator_code',
    indicatorKind: 'indicator_kind',
    dispositionRequires: 'disposition_requires',
    countsAsDisposition: 'counts_as_disposition',
    determinationOutcome: 'determination_outcome',
    requiresFilingRecord: 'requires_filing_record',
    permitsLaunch: 'permits_launch',
    deadlineId: 'deadline_id',
    clockTriggerEvent: 'clock_trigger_event',
    requiresWith: 'requires_with',
    recordableOnlyWhenTrue: 'recordable_only_when_true',
    indicatorCatalogueField: 'indicator_catalogue_field',
    /**
     * Sprint 63.31.0 (UC-VN-48 S14) — on a date field whose `clock_trigger_event`
     * is a period's "or" anchor: the select field of the same template whose
     * options are that period's alternative start events. A person records which
     * event applies and its date; the platform chooses none. Each option names
     * the period it starts by `deadline_id` (`validate:packs`
     * `START_EVENT_FIELD_*`).
     */
    startEventField: 'start_event_field',
    /**
     * fix-sprint-20261026-0900 Finding 8 — on an option of a start-event select
     * (the field a `start_event_field` names): the field of the same template
     * whose recorded date the start date must equal (a calendar day in the
     * organisation's zone). A start naming that option is refused while the case
     * records no such date, or when the date differs. The rule is read off the
     * option, never off its value (`validate:packs` `DATE_EQUALS_FIELD_*`).
     */
    dateEqualsField: 'date_equals_field',
};
/** Guidance tag selecting the pack's boundary statements for this register. */
export const AML_PROGRAMME_BOUNDARY_TAG = 'aml_programme_boundary';
// ── Collections ──
export const AML_PROGRAMME_SETTINGS_COLLECTION = 'grc_aml_programme_settings';
export const AML_SCOPING_RECORDS_COLLECTION = 'grc_aml_scoping_records';
export const AML_RETENTION_SCOPE_ROWS_COLLECTION = 'grc_aml_retention_scope_rows';
export const AML_INDICATOR_ROWS_COLLECTION = 'grc_aml_indicator_rows';
export const AML_INDICATOR_CONFIG_VERSIONS_COLLECTION = 'grc_aml_indicator_config_versions';
export const AML_CASES_COLLECTION = 'grc_aml_cases';
export const AML_BENEFICIAL_OWNER_RECORDS_COLLECTION = 'grc_aml_beneficial_owner_records';
export const AML_PRODUCT_LAUNCHES_COLLECTION = 'grc_aml_product_launches';
export const AML_LAUNCH_ASSESSMENTS_COLLECTION = 'grc_aml_launch_assessments';
/** Sprint 63.31.0 (#436 L4) — review tasks a launch assessment raises on the indicator rows it affects. */
export const AML_INDICATOR_REVIEW_TASKS_COLLECTION = 'grc_aml_indicator_review_tasks';
// ── Unions ──
/** A versioned record is `current` until a newer version supersedes it; superseded versions are kept. */
export const AML_RECORD_VERSION_STATUSES = ['current', 'superseded'];
/**
 * A case's lifecycle. Server-computed, never accepted from a request.
 * `open` → `determined` (a person's determination) → `approved` → `closed`.
 */
export const AML_CASE_STATUSES = ['open', 'determined', 'approved', 'closed'];
/**
 * How a closed case closed. `reported` needs a filing record; `not_reported`
 * needs a no-grounds outcome; `merged` (Sprint 63.31.0, #436 S14) names the
 * case of this organisation it was folded into, and is refused on a case that
 * holds a filing record.
 */
export const AML_CASE_CLOSURES = ['reported', 'not_reported', 'merged'];
/**
 * What a disposition option records, read from its `determination_outcome`
 * attribute. `undetermined` is an option that is not a determination at all
 * (it can be offered but never recorded as one).
 */
export const AML_DETERMINATION_OUTCOMES = ['grounds', 'no_grounds', 'undetermined'];
/**
 * One due date's state.
 * - `computed` — the anchor is recorded and every condition the clock names holds.
 * - `anchor_not_recorded` — the date the clock runs from is not recorded; renders *not recorded*.
 * - `condition_not_met` — the clock applies only under recorded conditions that do not hold; the UI omits the entry.
 * - `unit_not_computable` — the declared unit is one the platform cannot count.
 */
export const AML_DUE_DATE_STATES = [
    'computed',
    'anchor_not_recorded',
    'condition_not_met',
    'unit_not_computable',
];
/**
 * Whether the clock binds today (`live`) or from a future date (`upcoming`),
 * resolved from the declaring framework's record through the activation
 * resolver. `framework_record_unreadable` — the record states no date the
 * clock binds from, so whether it binds is not known; never collapsed into
 * either of the other two (Sprint 62.26.0, locked decision 3).
 */
export const AML_DUE_DATE_TIMINGS = ['live', 'upcoming', 'framework_record_unreadable'];
/** A launch record's state. `closed_not_reached` — a person determined the launch is out of scope. */
export const AML_LAUNCH_STATUSES = ['proposed', 'approved', 'closed_not_reached'];
export const AML_LAUNCH_ASSESSMENT_STATUSES = ['draft', 'decided'];
export const AML_LAUNCH_GATE_OUTCOMES = ['approved', 'refused'];
/**
 * Why the launch gate refused.
 * - `scope_not_determined` — no person has recorded whether the launch is in scope.
 * - `no_permitting_assessment` — no linked assessment carries a decision that permits the launch.
 * - `assessment_not_before_launch` — the permitting decision is not dated strictly before the launch date.
 */
export const AML_LAUNCH_GATE_REFUSAL_REASONS = [
    'scope_not_determined',
    'no_permitting_assessment',
    'assessment_not_before_launch',
];
/** An indicator row is a catalogue indicator the pack lists, or one the organisation defined itself. */
export const AML_INDICATOR_ROW_KINDS = ['statutory', 'entity_defined'];
/** A case trigger may also be an option drawn from another provision the template lists. */
export const AML_CASE_INDICATOR_KINDS = ['statutory', 'entity_defined', 'other_provision'];
/**
 * An indicator row as the register reads it.
 * - `recorded` — the organisation holds a row for it.
 * - `not_recorded` — the pack lists it and the organisation has recorded nothing.
 * - `no_longer_declared` — the organisation holds a row whose code the installed pack no longer lists.
 */
export const AML_INDICATOR_ROW_STATES = ['recorded', 'not_recorded', 'no_longer_declared'];
/**
 * Whether a row is disposed of for version approval.
 * - `disposed` — a coverage option that counts as a disposition, with every field it requires filled.
 * - `incomplete` — such an option, with required fields empty (named in `missing_fields`).
 * - `not_disposed` — no coverage, or a coverage option that is not a disposition.
 */
export const AML_DISPOSITION_STATES = ['disposed', 'incomplete', 'not_disposed'];
/** An entity-defined indicator's authority notification: recorded, or *notification not recorded*. */
export const AML_NOTIFICATION_STATES = ['recorded', 'not_recorded'];
/**
 * Sprint 63.31.0 (#436 L4) — a review task on one indicator row, raised from a
 * launch assessment. `open` until a person records the review; never closed by
 * the platform.
 */
export const AML_INDICATOR_REVIEW_TASK_STATUSES = ['open', 'completed'];
/**
 * Sprint 63.31.0 (#436 S6) — how one indicator row differs between two
 * configurations (an approved version and the one before it, or the draft and
 * the latest approved version). Computed on read, never stored.
 */
export const AML_CONFIG_CHANGE_KINDS = ['added', 'removed', 'changed'];
/**
 * Sprint 63.31.0 (#436 7a) — a records-retention scope entry's sign-off,
 * derived from the approvals recorded on its current content. Two different
 * people must sign while the organisation's two-person rule is on; one while
 * it is off. An edit of the entry clears its approvals.
 */
export const AML_RETENTION_APPROVAL_STATES = [
    'not_approved',
    'partially_approved',
    'approved',
];
/** A retention-scope entry per record class: recorded, or *not recorded*. */
export const AML_RETENTION_SCOPE_STATES = ['recorded', 'not_recorded'];
/**
 * Where the effective two-person policy comes from. The rule FAILS CLOSED
 * (gate E12.5 pass 1, P1-1): it is on unless the organisation recorded it off,
 * with a reason, through the setting that needs `aml_case:approve`. Nothing
 * else — no scoping record, a re-scoped role, a pack default of `false` or an
 * edited template — can turn it off.
 * - `org_setting` — the organisation recorded it (on, or off with a reason).
 * - `pack_default` — no setting; on, and the current scoping role's
 *   `two_person_policy_default` is `true`, which is why.
 * - `fail_closed_default` — no setting and no role default of `true` (no
 *   scoping record, or a role declaring none or `false`): on anyway.
 */
export const AML_TWO_PERSON_POLICY_SOURCES = [
    'org_setting',
    'pack_default',
    'fail_closed_default',
];
/** Why a row blocks version approval (the `missing[]` of a 409 `configuration_incomplete`). */
export const AML_CONFIGURATION_GAP_REASONS = [
    'no_coverage',
    'coverage_not_a_disposition',
    'required_field_empty',
];
/** fix-sprint-20261003-0545 F9 — the review duties the case register can raise. */
export const AML_CASE_REVIEW_DUTY_KINDS = [
    /**
     * The two-person rule was turned on while the determiner approved, and the
     * case moved past `approved` (a filing recorded, or closed) before the
     * approval could be taken back: the approval stands under an enabled rule.
     */
    'two_person_rule_conflict',
];
/**
 * fix-sprint-20261003-0545 F5 — the `sla_type` that marks an installed deadline
 * as a record-retention period. A platform deadline category, not an
 * instrument's term. A retention deadline APPLIES to a case's evidence when it
 * carries this `sla_type` AND its `linked_form_id` is a template of the case
 * surface or the records-retention-scope surface; its anchor is the case field
 * whose template `clock_trigger_event` equals the deadline's `trigger_event`.
 */
export const AML_RECORD_RETENTION_SLA_TYPE = 'record_retention';
/** The most evidence files one case read describes. */
export const AML_CASE_EVIDENCE_HOLD_FILE_CAP = 200;
// ── Error codes ──
/**
 * One code per condition, because a consumer branches on the condition (the
 * `PAYMENTS_EVIDENCE_ERROR_CODES` convention: UPPER key, lower-snake value).
 */
export const AML_PROGRAMME_ERROR_CODES = {
    // ── Records ────────────────────────────────────────────────
    CASE_NOT_FOUND: 'aml_case_not_found',
    RETENTION_SCOPE_ROW_NOT_FOUND: 'aml_retention_scope_row_not_found',
    RETENTION_SCOPE_ROW_EXISTS: 'aml_retention_scope_row_exists',
    INDICATOR_ROW_NOT_FOUND: 'aml_indicator_row_not_found',
    CONFIG_VERSION_NOT_FOUND: 'aml_indicator_config_version_not_found',
    BENEFICIAL_OWNER_RECORD_NOT_FOUND: 'aml_beneficial_owner_record_not_found',
    BENEFICIAL_OWNER_RECORD_EXISTS: 'aml_beneficial_owner_record_exists',
    LAUNCH_NOT_FOUND: 'aml_launch_not_found',
    LAUNCH_ASSESSMENT_NOT_FOUND: 'aml_launch_assessment_not_found',
    // ── Installed content ──────────────────────────────────────
    /** No operational installed template declares the register surface. */
    SURFACE_NOT_DECLARED: 'register_surface_not_declared',
    /** A value that is not an option of the installed field. */
    UNKNOWN_OPTION: 'unknown_option',
    // ── References ─────────────────────────────────────────────
    UNKNOWN_USER: 'unknown_user',
    UNKNOWN_LICENCE: 'unknown_licence',
    UNKNOWN_DILIGENCE_LINK: 'unknown_diligence_link',
    /** Sprint 63.31.0 (#436 S14) — a merge target that is not another case of this organisation. */
    UNKNOWN_CASE: 'unknown_case',
    /** Sprint 63.31.0 (#436 L4). */
    REVIEW_TASK_NOT_FOUND: 'aml_indicator_review_task_not_found',
    /** A catalogue or entity-defined trigger that is not in the approved configuration. */
    INDICATOR_NOT_IN_APPROVED_CONFIGURATION: 'indicator_not_in_approved_configuration',
    // ── Programme state ────────────────────────────────────────
    /** The scoping role carries `programme_inactive`; new cases are refused, existing ones stay readable. */
    PROGRAMME_INACTIVE: 'programme_inactive',
    INACTIVE_REASON_REQUIRED: 'inactive_reason_required',
    TWO_PERSON_POLICY_REASON_REQUIRED: 'two_person_policy_reason_required',
    /**
     * Recording a scoping role that deactivates the programme (and so refuses new
     * cases) needs `aml_case:approve` beside `payments_evidence:write`.
     */
    SCOPING_DEACTIVATION_NOT_AUTHORISED: 'scoping_deactivation_not_authorised',
    // ── Field rules read from the pack ─────────────────────────
    /** A field declaring `requires_with` was filled without its companion. */
    COMPANION_FIELD_REQUIRED: 'companion_field_required',
    /** A field declaring `recordable_only_when_true` was filled while a named boolean is not `true`. */
    FIELD_NOT_RECORDABLE: 'field_not_recordable',
    // ── Indicator configuration ────────────────────────────────
    /** A row lacks a disposition; `details.missing` lists each. */
    CONFIGURATION_INCOMPLETE: 'configuration_incomplete',
    // ── Case lifecycle ─────────────────────────────────────────
    INVALID_STATE_TRANSITION: 'invalid_state_transition',
    /** The disposition's `determination_outcome` is neither `grounds` nor `no_grounds`. */
    DISPOSITION_NOT_DETERMINATIVE: 'disposition_not_determinative',
    /**
     * Under the two-person policy the determiner may not approve. Sprint 63.31.0
     * (#435): nor may anyone who edited an indicator configuration draft since the
     * last approved version approve it, the decider of a launch's permitting
     * assessment approve the launch, or one person sign a retention-scope entry
     * twice.
     */
    SELF_APPROVAL_REFUSED: 'self_approval_refused',
    /**
     * Gate E12.5 pass 1 (P3-L5, UC-VN-49 L1) — an assessment decision dated
     * before the recorded procedure version came into force.
     */
    PROCEDURE_NOT_IN_FORCE: 'procedure_not_in_force',
    /**
     * Gate E12.5 pass 1 (P3-C4) — the two-person rule is on and the people it
     * excludes could not be read, so the approval is refused (503, fail closed).
     */
    TWO_PERSON_RULE_UNVERIFIABLE: 'two_person_rule_unverifiable',
    /** Subject and trigger fields are locked after approval. */
    CASE_LOCKED: 'case_locked',
    /** A filing record before approval, or on a no-grounds outcome. */
    FILING_RECORD_NOT_ALLOWED: 'filing_record_not_allowed',
    /** A closure whose outcome, disposition or filing record does not support it. */
    CLOSURE_REQUIREMENTS_UNMET: 'closure_requirements_unmet',
    /**
     * fix-sprint-20261003-0545 F5 — evidence is removed through the case only
     * while the case is `open`; from `determined` on it is held until retention ends.
     */
    CASE_EVIDENCE_HELD: 'aml_case_evidence_held',
    /** The file named is not an evidence file of this case. */
    CASE_EVIDENCE_NOT_ON_CASE: 'aml_case_evidence_not_on_case',
    /**
     * Sprint 63.31.0 (UC-VN-48 S14) — no installed case template declares a
     * retention start-event field (`start_event_field`), so there is nothing a
     * retention start could be recorded against (409).
     */
    RETENTION_START_NOT_DECLARED: 'aml_case_retention_start_not_declared',
    /**
     * Gate E12.5 pass 2 (P2-N1) — a retention start may not move earlier than
     * the one in effect: an earlier start ends the period sooner and releases
     * the case's evidence sooner (409).
     */
    RETENTION_START_EARLIER: 'aml_case_retention_start_earlier',
    /** A retention start dated before the earliest date the case records (400). */
    RETENTION_START_BEFORE_CASE_ANCHOR: 'aml_case_retention_start_before_case_anchor',
    /** The report date named as the start while the case holds no filing record (409). */
    RETENTION_START_REPORT_NOT_RECORDED: 'aml_case_retention_start_report_not_recorded',
    /** The report date named with a date other than the filing record's report date (400). */
    RETENTION_START_REPORT_DATE_MISMATCH: 'aml_case_retention_start_report_date_mismatch',
    /** Under the two-person rule the person who recorded the start cannot confirm it (409). */
    RETENTION_START_SELF_CONFIRMATION_REFUSED: 'aml_case_retention_start_self_confirmation_refused',
    /** A confirmation with no recorded start waiting for one (409). */
    RETENTION_START_NOTHING_TO_CONFIRM: 'aml_case_retention_start_nothing_to_confirm',
    // ── Launch gate ────────────────────────────────────────────
    /** Refused, cited and audited; the attempt is recorded on the launch. */
    LAUNCH_GATE_REFUSED: 'launch_gate_refused',
    /** A decision without risks and controls, or not dated strictly before the launch. */
    ASSESSMENT_DECISION_INCOMPLETE: 'assessment_decision_incomplete',
    // ── Export ─────────────────────────────────────────────────
    /** Case narratives asked for without `aml_case:approve`. */
    CASE_NARRATIVES_NOT_AUTHORISED: 'case_narratives_not_authorised',
    // ── Generic ────────────────────────────────────────────────
    /** The record changed between this request's read and its write; re-read and retry. */
    CONCURRENT_CHANGE: 'concurrent_change',
    INVALID_DATE: 'invalid_date',
};
//# sourceMappingURL=aml-programme.js.map